import { useEffect } from 'react';
import { useIMask } from 'react-imask';
import { OutlinedInput, type OutlinedInputProps } from '@mui/material';
import {
  ControlWrapper,
  withControl,
} from '@unicorn/control';

const SEPARATOR_REPLACEMENTS = new Set([',', '\u044E', '\u042E']);
const ALLOWED_CHAR_REGEXP = /[\d.]/;
const HAS_DIGIT_REGEXP = /\d/;
const LEADING_ZERO_OCTET_REGEXP = /(^|\.)0\d/;
const LEADING_ZERO_OCTET_REPLACE_REGEXP = /(^|\.)0(\d)/g;

const normalizeSeparatorChar = (char: string) =>
  SEPARATOR_REPLACEMENTS.has(char) ? '.' : char;

const isAllowedChar = (char: string) => ALLOWED_CHAR_REGEXP.test(char);

const shouldSkipDot = (value: string) => !value || value.endsWith('.');

const shouldSkipLeadingZero = (value: string, char: string) =>
  value.length === 0 && char === '0';

const normalizeLeadingZeroOctets = (value: string) => {
  let normalizedValue = value;

  while (LEADING_ZERO_OCTET_REGEXP.test(normalizedValue)) {
    normalizedValue = normalizedValue.replace(
      LEADING_ZERO_OCTET_REPLACE_REGEXP,
      '$10.$2'
    );
  }

  return normalizedValue.split('.').slice(0, 4).join('.');
};

const prepareAppendedValue = (appended: string, currentValue: string) => {
  let preparedValue = '';
  let nextValue = currentValue;

  for (const rawChar of appended) {
    const char = normalizeSeparatorChar(rawChar);

    if (!isAllowedChar(char)) {
      continue;
    }

    if (char === '.') {
      if (shouldSkipDot(nextValue)) {
        continue;
      }

      preparedValue += char;
      nextValue += char;

      continue;
    }

    if (shouldSkipLeadingZero(nextValue, char)) {
      continue;
    }

    preparedValue += char;
    nextValue += char;
  }

  return preparedValue;
};

const prepareSingleChar = (rawChar: string, currentValue: string) => {
  const char = normalizeSeparatorChar(rawChar);

  if (!isAllowedChar(char)) {
    return '';
  }

  if (char === '.' && shouldSkipDot(currentValue)) {
    return '';
  }

  if (shouldSkipLeadingZero(currentValue, char)) {
    return '';
  }

  return char;
};

const normalizeAcceptedValue = (value: string) => {
  if (!HAS_DIGIT_REGEXP.test(value)) {
    return '';
  }

  const normalizedValue = normalizeLeadingZeroOctets(value);
  const [firstOctet = ''] = normalizedValue.split('.');

  if (firstOctet === '0') {
    return '';
  }

  return normalizedValue;
};

export const IpControl = withControl<OutlinedInputProps>(
  ({
    label,
    value,
    setValue,
    error,
    isError,
    required,
    readOnly,
    disabled,
    placeholder = '0.0.0.0',
    helperText,
    endAdornment,
    clearable,
    ...props
  }) => {
    const handleResetValue = () => setValue('');

    const handleAccept = (newValue: string, masked: { value: string }) => {
      const normalizedValue = normalizeAcceptedValue(newValue);

      if (!normalizedValue) {
        masked.value = '';
        setValue('');

        return;
      }

      if (normalizedValue !== newValue) {
        masked.value = normalizedValue;
      }

      setValue(normalizedValue);
    };

    const { ref, maskRef } = useIMask(
      {
        mask: '0.0.0.0',
        blocks: {
          0: {
            mask: Number,
            min: 0,
            max: 255,
            maxLength: 3,
            scale: 0,
            radix: '.',
            thousandsSeparator: '',
          },
        },
        prepare: (appended: string, masked: { value: string }) =>
          prepareAppendedValue(appended, masked.value),
        prepareChar: (rawChar, masked) => prepareSingleChar(rawChar, masked.value),
      },
      {
        onAccept: handleAccept,
      }
    );

    useEffect(() => {
      if (maskRef.current) {
        if (value !== maskRef.current.value) {
          maskRef.current.value = value || '';
        }
      }
    }, [value, maskRef]);

    return (
      <ControlWrapper
        label={label}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        isError={isError}
        error={error}
        helperText={helperText}
      >
        <OutlinedInput
          inputRef={ref}
          placeholder={placeholder}
          value={value}
          error={!readOnly && !disabled && isError}
          readOnly={readOnly}
          disabled={disabled}
          {...props}
        />
      </ControlWrapper>
    );
  }
);